import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { ProfileMenu } from './ProfileMenu';
import { NotificationBell } from '../notifications/NotificationBell';
import { Logo } from '../branding/Logo';

interface TopHeaderProps {
  title?: ReactNode;
  subtitle?: string;
  showBack?: boolean;
  onBack?: () => void;
  leftAction?: ReactNode;
  rightAction?: ReactNode;
  actions?: ReactNode; // Alias for rightAction
  transparent?: boolean;
  hideProfileMenu?: boolean;
}

export function TopHeader({
  title,
  subtitle,
  showBack = false,
  onBack,
  leftAction,
  rightAction,
  actions,
  transparent = false,
  hideProfileMenu = false,
}: TopHeaderProps) {
  const headerRightAction = rightAction || actions;
  return (
    <header
      className={`
        h-12 sm:h-14
        ${transparent ? 'bg-transparent' : 'bg-background-card border-b border-border'}
      `}
    >
      <div className="flex items-center justify-between h-full px-4">
        {/* Left side - back button, custom action, logo (mobile), or spacer */}
        <div className="min-w-[40px] flex items-center">
          {showBack ? (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="
                w-8 h-8 -ml-1
                flex items-center justify-center
                rounded-full
                text-text-primary
                hover:bg-background
                transition-colors
              "
            >
              <ChevronLeft size={20} />
            </motion.button>
          ) : leftAction ? (
            leftAction
          ) : (
            <div className="lg:hidden">
              <Logo className="h-8 w-8" iconOnly />
            </div>
          )}
        </div>

        {/* Center - title */}
        <div className="flex-1 text-center">
          {title && (
            <h1 className="font-heading text-sm sm:text-base font-semibold text-text-primary truncate">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-xs text-text-secondary truncate">{subtitle}</p>
          )}
        </div>

        {/* Right side - Notifications + ProfileMenu by default, or custom action */}
        <div className="min-w-[40px] flex items-center justify-end gap-1">
          {headerRightAction ? (
            headerRightAction
          ) : (
            <>
              <NotificationBell />
              {!hideProfileMenu && <ProfileMenu />}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
